import React, { RefObject, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import ConfirmationModal from '@ui-lib/components/ModalWindow/ConfirmationModal';
import EllipsedTypography from '@ui-lib/components/Tooltip/EllipsedTypography';
import { CONFIRMATION_MODAL_TYPE } from '@ui-lib/components/ModalWindow/ConfirmationModal/const';
import { createSx } from '@ui-lib/utils';
import Button from '@ui-lib/components/Button';
import { useModal } from '@web-shared/hooks';
import DistanceView from '@web-shared/elements/DistanceView';
import { DISTANCE_UNIT } from '@web-shared/consts/distance-units';

import { NearbyLocation } from '../../../../../../../../../oh-main/web/frontend/global/modules/Locations/api/types/NearbyLocation';

export interface NearbyLocationRef {
  showNearbyLocations: (locations: NearbyLocation[]) => void;
}

interface Props {
  modalRef: RefObject<NearbyLocationRef | null>;
  onSelect: (location: { id: number; label: string; lat: number; lng: number }) => void;
  onCreateNewLocation: () => void;
}

const sx = createSx({
  locationItem: {
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid',
    borderColor: 'neutral.outlineVariant'
  },
  distance: {
    width: 80
  },
  nameColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: 'calc((100% - 200px)/5*2)'
  },
  distanceColumn: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: 'calc((100% - 200px)/5*3)'
  },
  staticColumn: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: 60
  },
  button: {
    minWidth: 64,
  },
  title: {
    textAlign: 'left',
    mb: 2,
  }
});

const NearbyLocationsPopup = ({ modalRef, onSelect, onCreateNewLocation }: Props) => {
  const [open, handleOpen] = useModal();
  const { t } = useTranslation();
  const [locations, setLocations] = useState<NearbyLocation[]>([]);

  useImperativeHandle(modalRef, () => {
    return {
      showNearbyLocations: (locations: NearbyLocation[]) => {
        setLocations(locations);
        handleOpen();
      },
    };
  }, []);

  const onSubmit = () => {
    handleOpen();
    onCreateNewLocation();
  };

  return (
    <ConfirmationModal
      open={open}
      onClose={handleOpen}
      onSubmit={onSubmit}
      submitBtnLabel={t('yes')}
      title={t('addRouteExternalLocationConfirmation')}
      modalType={CONFIRMATION_MODAL_TYPE.WARNING}
      modalMaxHeight='950px'
    >
      <>
        <Typography sx={sx.title} variant='body2' data-test-id='nearby-locations-title'>
          {t('nextLocationNearby')}
        </Typography>
        {locations.map((location) => {
          const locationData = {
            id: location.id,
            label: location.label,
            lat: location.latitude,
            lng: location.longitude,
          };
          return <Stack key={location.id} direction='row' sx={sx.locationItem} data-test-id='nearby-location'>
            <EllipsedTypography
              testID='nearby-location-title'
              variant='body2Bold'
              sx={sx.nameColumn}
            >
              {location.label}
            </EllipsedTypography>
            <EllipsedTypography
              testID='nearby-location-address'
              variant='body2'
              sx={sx.distanceColumn}
            >
              {location.address}
            </EllipsedTypography>
            <EllipsedTypography
              testID='nearby-location-distance'
              variant='body2'
              sx={sx.staticColumn}
            >
              <DistanceView distance={location.distance} units={DISTANCE_UNIT.KM} />
            </EllipsedTypography>
            <Button
              data-test-id='select-nearby-location-btn'
              variant='text'
              underline
              color='secondary'
              onClick={() => onSelect(locationData)} sx={sx.button}>
              {t('select')}
            </Button>
          </Stack>;
        })}
      </>
    </ConfirmationModal>
  );
};

export default NearbyLocationsPopup;
